import { useEffect, useRef, useState } from 'react';

/** A number input that fixes the classic "stuck 0" problem: a plain
 * `<input type="number" value={x}>` bound straight to a numeric state can
 * never be made to look empty — clearing it re-parses to 0 (or whatever
 * the field's fallback is), which re-renders right back with that digit
 * still there, so backspacing it and typing a fresh number produces
 * "01" instead of "1". This tracks the raw keystrokes in local state, so
 * the field displays exactly what's typed while focused regardless of
 * what the committed value resolves to; only on blur (or when the value
 * changes from elsewhere, e.g. autofill) does it re-sync from the real
 * value. When that value is 0, it displays as an empty box with "0" as a
 * placeholder instead of a literal zero, so typing over it is one single
 * uninterrupted action — appropriate both for "0 means not set yet"
 * fields (fees, discounts) and for plain counts (kids, sort order). */
export default function NumberInput({
  value,
  onChange,
  className,
  min,
  max,
  step,
  placeholder = '0',
  id,
}: {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  min?: number;
  max?: number;
  step?: string | number;
  placeholder?: string;
  id?: string;
}) {
  const [draft, setDraft] = useState(value === 0 ? '' : String(value));
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setDraft(value === 0 ? '' : String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <input
      id={id}
      type="number"
      min={min}
      max={max}
      step={step}
      placeholder={placeholder}
      className={className}
      value={draft}
      onFocus={() => {
        focused.current = true;
      }}
      onBlur={() => {
        focused.current = false;
        setDraft(value === 0 ? '' : String(value));
      }}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        const parsed = raw === '' ? 0 : Number(raw);
        if (Number.isFinite(parsed)) onChange(parsed);
      }}
    />
  );
}
